"use client";

/**
 * UseCelo Client Provider - Client-Side Only Provider
 *
 * This provider includes all client-side functionality and sub-providers.
 * It should only be rendered on the client side after hydration.
 */

import { ReactNode, useContext, useEffect } from 'react';
import { FeatureProvider, useFeatures } from './FeatureProvider';
import { PaymentProvider, usePayment } from './PaymentProvider';
import { UsageTrackingProvider, useUsageTracking } from './UsageTrackingProvider';
import { FeatureGatingProvider } from './FeatureGatingProvider';
import { UseCeloContextType } from '../types';
import { UseCeloContext } from './UseCeloProvider';

interface UseCeloClientProviderProps {
  appId: string;
  publicKey: string;
  children: ReactNode;
  apiHost?: string;
  userId?: string | null | (() => string | null | Promise<string | null>);
  fallbackBehavior?: 'hide' | 'disable' | 'blur' | 'upgrade';
  cacheTimeout?: number;
  onFeatureBlock?: (featureName: string) => void;
  onUpgradeClick?: (featureName: string) => void;
  enablePayments?: boolean;
  autoLoadGatewayInfo?: boolean;
  onGatewayInfoLoaded?: (info: any) => void;
  onPaymentError?: (error: string) => void;
}

export function UseCeloClientProvider({
  userId,
  ...props
}: UseCeloClientProviderProps) {
  // Simplified: no more auth adapters, just pass userId through context

  return (
    <FeatureProvider
      appId={props.appId}
      publicKey={props.publicKey}
      apiHost={props.apiHost || 'https://usecelo.com'}
      cacheTimeout={props.cacheTimeout}
      userId={userId}
    >
      <UsageTrackingProvider
        appId={props.appId}
        publicKey={props.publicKey}
        apiHost={props.apiHost || 'https://usecelo.com'}
        userId={userId}
      >
        <PaymentProvider
          appId={props.appId}
          publicKey={props.publicKey}
          apiHost={props.apiHost || 'https://usecelo.com'}
          enabled={props.enablePayments || false}
          autoLoadGatewayInfo={props.autoLoadGatewayInfo}
          onGatewayInfoLoaded={props.onGatewayInfoLoaded}
          onPaymentError={props.onPaymentError}
          userId={userId}
        >
          <UseCeloClientInner {...props} userId={userId} />
        </PaymentProvider>
      </UsageTrackingProvider>
    </FeatureProvider>
  );
}

// Using shared UseCeloContext from main provider

/**
 * Inner component that composes the unified context from sub-providers
 */
function UseCeloClientInner({
  children,
  appId,
  publicKey,
  apiHost = "https://usecelo.com",
  fallbackBehavior = "hide",
  onFeatureBlock,
  onUpgradeClick,
  enablePayments = false,
  userId,
}: Pick<UseCeloClientProviderProps, 'children' | 'appId' | 'publicKey' | 'apiHost' | 'fallbackBehavior' | 'onFeatureBlock' | 'onUpgradeClick' | 'enablePayments' | 'userId'>) {

  // Get from individual providers
  const features = useFeatures();
  const payment = enablePayments ? usePayment() : null;
  const usageTracking = useUsageTracking();

  // Build unified context value
  const contextValue: UseCeloContextType = {
    // Feature state and methods
    features: features.features,
    loading: features.loading || (payment?.paymentLoading ?? false),
    error: features.error,
    userPlan: features.userPlan,
    subscription: features.subscription,
    checkAccess: features.checkAccess,
    getFeature: features.getFeature,
    hasFeature: features.hasFeature,
    refreshFeatures: features.refreshFeatures,

    // Auth - simplified approach
    userId,

    // Usage tracking
    trackUsage: usageTracking.trackUsage,
    trackSingleUsage: usageTracking.trackSingleUsage,

    // Configuration
    appId,
    publicKey,
    apiHost,

    // Payment integration (when enabled)
    ...(enablePayments && payment && {
      paymentClient: payment.paymentClient || undefined,
      gatewayInfo: payment.gatewayInfo,
      isPaymentConfigured: payment.isPaymentConfigured,
      createCheckout: payment.createCheckout,
      getCheckoutUrl: payment.getCheckoutUrl,
      createPortalSession: payment.createPortalSession,
      getSubscription: payment.getSubscription,
      cancelSubscription: payment.cancelSubscription,
      pauseSubscription: payment.pauseSubscription,
      resumeSubscription: payment.resumeSubscription,
      getPaymentMethods: payment.getPaymentMethods,
      refreshGatewayInfo: payment.refreshGatewayInfo,
    }),
  };

  // Expose global functions
  useEffect(() => {
    const { checkAccess, trackSingleUsage } = contextValue;
    (window as Window & { useCelo?: Record<string, unknown> }).useCelo = {
      ...((window as Window & { useCelo?: Record<string, unknown> }).useCelo || {}),
      checkAccess,
      trackUsage: trackSingleUsage,
    };
  }, [contextValue]);

  return (
    <UseCeloContext.Provider value={contextValue}>
      <FeatureGatingProvider
        fallbackBehavior={fallbackBehavior}
        onFeatureBlock={onFeatureBlock}
        onUpgradeClick={onUpgradeClick}
      >
        {children}
      </FeatureGatingProvider>
    </UseCeloContext.Provider>
  );
}